import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import { confirmAlert } from 'react-confirm-alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck, faTimes, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { getConciergeCuratorApplications, updateConciergeCuratorApplication } from '../../api/noir';
import { getTierDisplay } from '../../helpers/tier_helpers';
import { splitDbSocialLinksIntoIndividualLinks } from '../../helpers/social_helpers';
import Tooltip from '../General/Tooltip';
import moment from 'moment';
import './NoirWaitlist.scss';

const PAGE_SIZE = 50;

const getInstagramHandle = socialLinks => {
  const { instagram_url } = splitDbSocialLinksIntoIndividualLinks(socialLinks);
  if (!instagram_url) return null;
  return instagram_url.trim().replace(/\/$/, '').split('/').pop() || null;
};

const STATUS_FILTERS = [
  { value: 'all', display: 'All' },
  { value: 'pending', display: 'Pending' },
  { value: 'accepted', display: 'Accepted' },
  { value: 'rejected', display: 'Rejected' }
];

const COLUMNS = [
  { key: 'name', label: 'Creator' },
  { key: 'tier', label: 'Tier' },
  { key: 'score', label: 'Score' },
  { key: 'totalVolume', label: 'Volume' },
  { key: 'instagram', label: 'IG' },
  { key: 'pitch', label: 'Pitch' },
  { key: 'premiumShoppers', label: 'Gold/Black Shoppers' },
  { key: 'premiumGmv', label: 'Premium GMV' },
  { key: 'status', label: 'Status' },
  { key: 'createdAt', label: 'Applied' },
  { key: 'actions', label: '' }
];

const NoirWaitlist = () => {
  const [applications, setApplications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const debounceRef = useRef(null);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const fetchApplications = useCallback(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (search) params.search = search;

    getConciergeCuratorApplications(params)
      .then(({ applications: apps, totalCount: total }) => {
        setApplications(apps || []);
        setTotalCount(total);
      })
      .catch(() => window.ALERT.error('Failed to load waitlist applications.'))
      .finally(() => setLoading(false));
  }, [statusFilter, search, page]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, search]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = value.trim();
    debounceRef.current = setTimeout(() => setSearch(trimmed.length >= 2 ? trimmed : ''), 500);
  };

  const handleAccept = app => {
    confirmAlert({
      title: `Accept ${app.user?.name || 'this creator'}?`,
      message: 'Also invite them to the Noir slack and add their slack ID to their settings page.',
      buttons: [
        { label: 'Cancel', className: 'cancel', onClick: () => {} },
        {
          label: 'Accept',
          onClick: () => {
            setUpdatingId(app.id);
            updateConciergeCuratorApplication(app.id, { status: 'accepted' })
              .then(() => {
                window.ALERT.success(`Accepted ${app.user?.name || 'application'}.`);
                fetchApplications();
              })
              .catch(() => window.ALERT.error('Failed to accept application.'))
              .finally(() => setUpdatingId(null));
          }
        }
      ]
    });
  };

  const handleReject = app => {
    confirmAlert({
      customUI: ({ onClose }) => <RejectModal app={app} onClose={onClose} onSuccess={fetchApplications} setUpdatingId={setUpdatingId} />
    });
  };

  return (
    <div className='noir-waitlist'>
      <div className='waitlist-toolbar'>
        <div className='toolbar-left'>
          <div className='filter-tabs'>
            {STATUS_FILTERS.map(f => (
              <div key={f.value} className={cn('filter-tab', { active: statusFilter === f.value })} onClick={() => setStatusFilter(f.value)}>
                {f.display}
              </div>
            ))}
          </div>
          <div className='search-input-wrapper'>
            <FontAwesomeIcon icon={faSearch} className='search-icon' />
            <input type='text' className='search-input' placeholder='Search by name, username or ID...' value={searchInput} onChange={handleSearchChange} />
          </div>
        </div>
        <div className='waitlist-count'>{totalCount} application{totalCount !== 1 ? 's' : ''}</div>
      </div>

      {loading ? (
        <div className='waitlist-placeholder'>
          <FontAwesomeIcon icon={faSpinner} spin />
          Loading applications...
        </div>
      ) : !applications.length ? (
        <div className='waitlist-placeholder'>No applications found.</div>
      ) : (
        <>
          <div className='waitlist-table'>
            <div className='waitlist-grid header'>
              {COLUMNS.map(col => (
                <div key={col.key} className='th'>
                  {col.label}
                </div>
              ))}
            </div>
            {applications.map(app => {
              const igHandle = getInstagramHandle(app.user?.social_links);
              return (
              <div key={app.id} className='waitlist-grid row'>
                <div className='td creator-name'>
                  {app.user?.name || `User #${app.User_id}`}
                  <span className='user-id'>ID: {app.User_id}</span>
                </div>
                <div className='td'>{app.tier ? getTierDisplay(app.tier) : '-'}</div>
                <div className='td'>{app.tierScore != null ? Number(app.tierScore).toFixed(1) : '-'}</div>
                <div className='td'>{app.totalVolume ? `$${Number(app.totalVolume).toLocaleString()}` : '$0'}</div>
                <div className='td ig-cell'>
                  {igHandle ? (
                    <a href={`https://instagram.com/${igHandle}`} target='_blank' rel='noopener noreferrer'>
                      @{igHandle}
                    </a>
                  ) : (
                    '-'
                  )}
                </div>
                <div className='td pitch-cell' title={app.pitch}>
                  {app.pitch || '-'}
                </div>
                <div className='td'>{app.premiumShopperCount || 0}</div>
                <div className='td'>{app.premiumShopperGmv ? `$${Number(app.premiumShopperGmv).toLocaleString()}` : '$0'}</div>
                <div className='td'>
                  <span className={cn('status-badge', app.status)}>{app.status}</span>
                </div>
                <div className='td'>{app.createdAt ? moment(app.createdAt).format('MMM D, YYYY') : '-'}</div>
                <div className='td actions'>
                  {app.status === 'pending' && (
                    updatingId === app.id ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <>
                        <Tooltip message='Approve'>
                          <div className={cn('action-icon accept', { disabled: !!updatingId })} onClick={() => !updatingId && handleAccept(app)}>
                            <FontAwesomeIcon icon={faCheck} />
                          </div>
                        </Tooltip>
                        <Tooltip message='Deny'>
                          <div className={cn('action-icon reject', { disabled: !!updatingId })} onClick={() => !updatingId && handleReject(app)}>
                            <FontAwesomeIcon icon={faTimes} />
                          </div>
                        </Tooltip>
                      </>
                    )
                  )}
                </div>
              </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className='waitlist-pagination'>
              <span className='pagination-info'>
                Page {page + 1} of {totalPages}
              </span>
              <div className='pagination-buttons'>
                <button disabled={page === 0} onClick={() => setPage(page - 1)}>
                  Previous
                </button>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RejectModal = ({ app, onClose, onSuccess, setUpdatingId }) => {
  const [adminNotes, setAdminNotes] = useState('');

  const handleSubmit = () => {
    onClose();
    setUpdatingId(app.id);
    updateConciergeCuratorApplication(app.id, { status: 'rejected', adminNotes: adminNotes || null })
      .then(() => {
        window.ALERT.success(`Rejected ${app.user?.name || 'application'}.`);
        onSuccess();
      })
      .catch(() => window.ALERT.error('Failed to reject application.'))
      .finally(() => setUpdatingId(null));
  };

  return (
    <div className='noir-waitlist-reject-modal react-confirm-alert-body'>
      <h1>Reject {app.user?.name || 'this creator'}?</h1>
      <div className='reject-form'>
        <label>
          Notes <span className='optional'>optional</span>
          <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder='Reason for rejection...' rows={3} />
        </label>
      </div>
      <div className='react-confirm-alert-button-group'>
        <button className='cancel-btn' onClick={onClose}>
          Cancel
        </button>
        <button onClick={handleSubmit}>Reject</button>
      </div>
    </div>
  );
};

export default NoirWaitlist;
